import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DollarSign, FileText, TrendingUp, Filter } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { License, Application } from "@shared/schema";
import { useCurrency } from "@/hooks/use-currency";

type BillingRecord = {
  id: number;
  type: "historical" | "current" | "future";
  date: Date;
  licenseName: string;
  applicationName: string;
  amount: number;
  currency: string;
  status?: string;
  notes?: string;
  licenseId: number;
};

export default function BillingHistory() {
  const [filterType, setFilterType] = useState<"all" | "historical" | "current" | "future">("all");
  const { baseCurrency } = useCurrency();

  // Fetch all licenses
  const { data: licenses = [], isLoading: licensesLoading } = useQuery<License[]>({
    queryKey: ['/api/licenses'],
  });

  // Fetch all applications
  const { data: applications = [] } = useQuery<Application[]>({
    queryKey: ['/api/applications'],
  });

  // Fetch all historical renewals
  const { data: allHistoricalRenewals = [], isLoading: renewalsLoading } = useQuery({
    queryKey: ['/api/historical-renewals/all'],
    queryFn: async () => {
      // Fetch historical renewals for all licenses
      const promises = licenses.map(license => 
        fetch(`/api/licenses/${license.id}/historical-renewals`)
          .then(res => res.ok ? res.json() : [])
          .catch(() => [])
      );
      const results = await Promise.all(promises);
      return results.flat();
    },
    enabled: licenses.length > 0,
  });

  // Create a map of applications for quick lookup
  const applicationMap = new Map(applications.map(app => [app.id, app]));

  // Combine all billing data
  const allBillingRecords: BillingRecord[] = [
    // Historical renewals
    ...allHistoricalRenewals.map((renewal: any) => {
      const license = licenses.find(l => l.id === renewal.licenseId);
      const app = license ? applicationMap.get(license.applicationId) : null;
      return {
        id: renewal.id,
        type: "historical" as const,
        date: new Date(renewal.renewalDate),
        licenseName: license?.name || "Unknown",
        applicationName: app?.name || "Unknown",
        amount: renewal.newCost,
        currency: renewal.currency || "USD",
        notes: renewal.notes,
        licenseId: renewal.licenseId,
      };
    }),
    // Current licenses
    ...licenses
      .filter(license => license.status === "active")
      .map(license => {
        const app = applicationMap.get(license.applicationId);
        return {
          id: license.id,
          type: "current" as const,
          date: new Date(license.startDate),
          licenseName: license.name,
          applicationName: app?.name || "Unknown",
          amount: license.cost,
          currency: license.currency,
          status: license.status,
          licenseId: license.id,
        };
      }),
    // Future renewals
    ...licenses
      .filter(license => license.renewalDate && new Date(license.renewalDate) > new Date())
      .map(license => {
        const app = applicationMap.get(license.applicationId);
        return {
          id: license.id,
          type: "future" as const,
          date: new Date(license.renewalDate!),
          licenseName: license.name,
          applicationName: app?.name || "Unknown",
          amount: license.cost,
          currency: license.currency,
          status: license.status,
          licenseId: license.id,
        };
      }),
  ].sort((a, b) => b.date.getTime() - a.date.getTime()); // Sort by date descending

  // Filter records
  const filteredRecords = filterType === "all" 
    ? allBillingRecords 
    : allBillingRecords.filter(record => record.type === filterType);

  // Calculate totals
  const calculateTotals = (records: BillingRecord[]) => {
    return records.reduce((sum, record) => sum + record.amount, 0);
  };

  const historicalTotal = calculateTotals(allBillingRecords.filter(r => r.type === "historical"));
  const currentTotal = calculateTotals(allBillingRecords.filter(r => r.type === "current"));
  const futureTotal = calculateTotals(allBillingRecords.filter(r => r.type === "future"));

  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case "historical": return "bg-gray-500";
      case "current": return "bg-green-500";
      case "future": return "bg-blue-500";
      default: return "bg-gray-500";
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Billing History</h1>
        <p className="text-muted-foreground mt-1">
          View all billing data including historical renewals, current licenses, and future renewals
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Historical Spend</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-historical-total">
              {formatCurrency(historicalTotal)}
            </div>
            <p className="text-xs text-muted-foreground">
              {allBillingRecords.filter(r => r.type === "historical").length} records
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Current Licenses</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-current-total">
              {formatCurrency(currentTotal)}
            </div>
            <p className="text-xs text-muted-foreground">
              {allBillingRecords.filter(r => r.type === "current").length} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Upcoming Renewals</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-future-total">
              {formatCurrency(futureTotal)}
            </div>
            <p className="text-xs text-muted-foreground">
              {allBillingRecords.filter(r => r.type === "future").length} upcoming
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filter and Records Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Billing Records</CardTitle>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={filterType} onValueChange={(value: any) => setFilterType(value)}>
                <SelectTrigger className="w-[180px]" data-testid="select-filter-type">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Records</SelectItem>
                  <SelectItem value="historical">Historical Only</SelectItem>
                  <SelectItem value="current">Current Only</SelectItem>
                  <SelectItem value="future">Future Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <CardDescription>
            {filteredRecords.length} records • Total: {formatCurrency(calculateTotals(filteredRecords))}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {licensesLoading || renewalsLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading billing data...</div>
          ) : filteredRecords.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No billing records found. Add a license or historical renewal to get started.
            </div>
          ) : (
            <div className="space-y-3">
              {filteredRecords.map((record, index) => (
                <div 
                  key={`${record.type}-${record.id}-${index}`}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  data-testid={`row-billing-record-${index}`}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="flex flex-col items-center justify-center w-16">
                      <div className="text-2xl font-bold">{record.date.getDate()}</div>
                      <div className="text-xs text-muted-foreground uppercase">
                        {record.date.toLocaleString('default', { month: 'short' })}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {record.date.getFullYear()}
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold" data-testid={`text-license-name-${index}`}>
                          {record.licenseName}
                        </h4>
                        <Badge className={getTypeBadgeColor(record.type)} data-testid={`badge-type-${index}`}>
                          {record.type}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{record.applicationName}</p>
                      {record.notes && (
                        <p className="text-xs text-muted-foreground mt-1">{record.notes}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-lg font-semibold" data-testid={`text-amount-${index}`}>
                      {formatCurrency(record.amount)}
                    </div>
                    <div className="text-xs text-muted-foreground">{record.currency}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
